// @ts-nocheck
import Button from 'atoms/button/button';
import Loader from 'atoms/loader';
import _ from 'lodash';
import * as advertisingSelectors from 'models/advertisings/selector';
import * as dynamicGridSelectors from 'models/dynamicGrid/selector';
import Stack from 'molecules/stack/stack';
import StackItem from 'molecules/stackItem/stackItem';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as dynamicGridSagaActions from 'models/dynamicGrid/sagaActions';
import { getState } from 'reducers';
import ThetaGrid from 'templates/thetaGrid';
import { Tprops } from './types';
import * as adModalSelectors from 'pages/advertising/adModal/selector';
import { AD_MODAL_TABLE_IDS } from './constants';
import Scrollable from 'atoms/scrollable/scrollable';
import ModalBody from 'atoms/modal/modalBody';
import ModalActions from 'atoms/modal/modalActions';
import ThetaGridPaginated from 'templates/thetaGridPaginated';
import { REQUESTS } from 'utils/services/thetaretail/requests';
import { getTotalCount } from 'models/dynamicGrid/selector';

const SelectStores = (props: Tprops) => {
	const { hideModal, handleNextClick, goBack } = props;
	const name = 'adStores';
	const tableId = AD_MODAL_TABLE_IDS.STORES;
	const isLoading = useSelector((state) => advertisingSelectors.isLoading(state, name));
	const totalCount = useSelector((state) => getTotalCount(state, tableId));
	const selectedStores = useSelector((state) => dynamicGridSelectors.getTableSelection(state, tableId));
	const adData: any = useSelector((state) => adModalSelectors.getAdModalData(state, 'advertising')) || {};

	const isEditMode = useSelector((state) => adModalSelectors.getEditMode(state));

	const preSelectedStoreIds = useMemo(() => {
		if (!adData?.SelectedStores) return [];
		const mappedArray = adData.SelectedStores.split(',').map((id) => Number(id.trim()));
		return _.uniq(mappedArray);
	}, [adData?.SelectedStores]);

	// Create extendedSearchFilters for edit mode with Bulk In operator
	const extendedSearchFilters = useMemo(() => {
		if (!isEditMode || !preSelectedStoreIds?.length) {
			return null;
		}

		// Format as single-quoted strings: '0114','0005','1241'
		const filterQuery = preSelectedStoreIds.map((id) => `'${id}'`).join(',');

		return [
			{
				id: `store-filter-${Date.now()}`,
				query: filterQuery,
				criteria: {
					displayName: 'StoreID',
					value: 'StoreID',
					queryName: 'StoreID',
					rowIdentifier: 'StoreID',
					type: 'text',
					label: 'StoreID',
				},
				operator: {
					value: 'bulk.in',
					displayName: 'Bulk In',
				},
				isLocked: false, // Explicitly set to false to ensure filter tag displays
			},
		];
	}, [isEditMode, preSelectedStoreIds]);

	const dispatch = useDispatch();
	const filtersClearedRef = useRef(false);

	// Clear Store filters when creating a new ad (not in edit mode)
	useEffect(() => {
		if (!isEditMode) {
			if (!filtersClearedRef.current) {
				const state = getState();
				const currentFilters = dynamicGridSelectors.getGridFilters(state, tableId);

				const hasStoreFilter = currentFilters?.search?.some(
					(filter) => filter?.criteria?.rowIdentifier === 'StoreID'
				);

				// If Store filters exist, remove them
				if (hasStoreFilter) {
					const filteredSearch = (currentFilters.search || []).filter(
						(filter) => filter?.criteria?.rowIdentifier !== 'StoreID'
					);

					dispatch(
						dynamicGridSagaActions.updateGridFilters(
							tableId,
							{
								...currentFilters,
								search: filteredSearch,
								pageNumber: 1,
							},
							true,
							1,
							false // isStorageEnabled = false
						)
					);
				}
				filtersClearedRef.current = true;
			}
		} else {
			// Reset the ref when entering edit mode
			filtersClearedRef.current = false;
		}
	}, [isEditMode, tableId, dispatch]);

	const gridConfig = {
		enableSelection: true,
		enableSorting: true,
		preSelectedRowIds: preSelectedStoreIds,
	};

	const templateConfig = {
		gridConfig,
		isFilterEnabled: true,
		tableId: tableId,
		selectRowKey: 'StoreID',
		extendedSearchFilters: extendedSearchFilters,
		dataProviderOptions: {
			type: 'POST',
			version: 2,
			...REQUESTS.POST.GET_VALID_STORES,
			pathParams: { customerId: adData.CustomerID },
		},
	};

	const [ref, handleUpdateRef] = useState(null);

	const renderLoader = () => {
		return (
			<StackItem isGrow isStretch align="center">
				<Loader />
			</StackItem>
		);
	};

	return (
		<>
			<ModalBody onDomRef={handleUpdateRef}>
				<Stack isVertical gutter="Large" className="store-selection">
					{isLoading ? (
						renderLoader()
					) : (
						<ThetaGridPaginated isStorageEnabled={false} parent={ref} config={templateConfig} />
					)}
				</Stack>
			</ModalBody>
			<ModalActions>
				<Stack gutter="medium">
					<StackItem isGrow isStretch>
						<Stack align="left" gutter="small">
							<StackItem>
								<Button isText isSecondary name="Reset Ad Info" handleClick={goBack} />
							</StackItem>
						</Stack>
					</StackItem>
					<StackItem>
						<Stack align="right" gutter="small">
							<StackItem>
								<Button isText isSecondary name="Cancel" handleClick={hideModal} />
							</StackItem>
							<StackItem isStretch align="right">
								<Button
									isPrimary
									disabled={!selectedStores?.selectedRowKeys?.length}
									name={'Select SKUs'}
									handleClick={() => {
										if (
											selectedStores?.isSelectAll ||
											selectedStores?.selectedRowKeys?.length === totalCount
										) {
											localStorage.setItem('selectAllStoresCount', totalCount);
										}
										handleNextClick();
									}}
								/>
							</StackItem>
						</Stack>
					</StackItem>
				</Stack>
			</ModalActions>
		</>
	);
};

export default SelectStores;
